import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Mail } from 'lucide-react';
import Modal from 'components/ui/Modal';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';

export type RecipientKind = 'phone' | 'email';

export interface Recipient {
  value: string;
  kind: RecipientKind;
}

export interface InviteRoleOption {
  value: string;
  label: string;
  description: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Classify a pasted token as an email, a phone, or invalid. */
export function detectRecipient(raw: string): Recipient | null {
  const token = raw.trim().replace(/,$/, '');
  if (!token) return null;
  if (EMAIL_RE.test(token)) return { value: token, kind: 'email' };
  const digits = token.replace(/[^\d]/g, '');
  if (/^[+\d][\d\s()-]*$/.test(token) && digits.length >= 7) {
    return { value: token, kind: 'phone' };
  }
  return null;
}

export interface InviteMembersModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  roles: InviteRoleOption[];
  defaultRole?: string;
  /** Footer note under the role picker. */
  infoText?: string;
  submitting?: boolean;
  /** Optional field rendered above the chip input (e.g. a group selector). */
  leadingField?: React.ReactNode;
  /** Disable send beyond the recipient check (e.g. no group chosen). */
  sendDisabled?: boolean;
  /** Split by kind so the caller routes phones→SMS and emails→link. */
  onSubmit: (recipients: Recipient[], role: string) => void;
}

/** Phone → SMS, email → link is the intent; the caller's `onSubmit` performs the real send. */
export const InviteMembersModal: React.FC<InviteMembersModalProps> = ({
  open,
  onClose,
  title,
  roles,
  defaultRole,
  infoText,
  submitting = false,
  leadingField,
  sendDisabled = false,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [draft, setDraft] = useState('');
  const [role, setRole] = useState(defaultRole ?? roles[0]?.value ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  const roleLabel = useMemo(() => roles.find((r) => r.value === role)?.label ?? '', [roles, role]);

  const reset = () => {
    setRecipients([]);
    setDraft('');
    setRole(defaultRole ?? roles[0]?.value ?? '');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const commit = (raw: string) => {
    // Support pasting several separated by comma / whitespace / newline.
    const tokens = raw.split(/[\s,;]+/).filter(Boolean);
    if (!tokens.length) return;
    setRecipients((prev) => {
      const seen = new Set(prev.map((r) => r.value.toLowerCase()));
      const next = [...prev];
      for (const tok of tokens) {
        const rec = detectRecipient(tok);
        if (rec && !seen.has(rec.value.toLowerCase())) {
          seen.add(rec.value.toLowerCase());
          next.push(rec);
        }
      }
      return next;
    });
    setDraft('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      if (draft.trim()) {
        e.preventDefault();
        commit(draft);
      }
    } else if (e.key === 'Backspace' && !draft) {
      setRecipients((prev) => prev.slice(0, -1));
    }
  };

  const remove = (value: string) =>
    setRecipients((prev) => prev.filter((r) => r.value !== value));

  const count = recipients.length;

  const handleSend = () => {
    if (draft.trim()) commit(draft);
    if (!count && !draft.trim()) return;
    onSubmit(recipients.length ? recipients : (detectRecipient(draft) ? [detectRecipient(draft)!] : []), role);
  };

  return (
    <Modal isOpen={open} onClose={handleClose} title={title ?? t('invite_members_title')}>
      <div className="space-y-5">
        {leadingField}

        {/* Chip input */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">
            {t('invite_phone_or_email')}
          </label>
          <div
            role="group"
            onClick={() => inputRef.current?.focus()}
            className="flex min-h-[42px] flex-wrap items-center gap-1.5 rounded-lg border border-input bg-background px-2 py-1.5 ring-offset-background transition-colors focus-within:border-foreground/40 focus-within:ring-1 focus-within:ring-ring"
          >
            {recipients.map((r) => (
              <span
                key={r.value}
                className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-[13px] font-medium text-foreground"
              >
                {r.value}
                <button
                  type="button"
                  aria-label={t('close')}
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(r.value);
                  }}
                  className="rounded-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => draft.trim() && commit(draft)}
              onPaste={(e) => {
                e.preventDefault();
                commit(e.clipboardData.getData('text'));
              }}
              placeholder={count ? t('invite_add_another') : t('invite_add_placeholder')}
              className="min-w-[120px] flex-1 border-0 bg-transparent px-1 py-0.5 text-sm outline-none focus-visible:!outline-none placeholder:text-muted-foreground"
            />
          </div>
        </div>

        {/* Role picker */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">{t('invite_assign_role')}</label>
          <div className="grid grid-cols-3 gap-2">
            {roles.map((r) => {
              const selected = r.value === role;
              return (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRole(r.value)}
                  className={cn(
                    'rounded-lg border px-3 py-2.5 text-left transition-colors',
                    selected
                      ? 'border-foreground ring-1 ring-foreground'
                      : 'border-border hover:border-foreground/20 hover:bg-muted/40',
                  )}
                >
                  <span className="block text-sm font-semibold text-foreground">
                    {r.label}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{r.description}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Info line */}
        {infoText && (
          <div className="flex items-start gap-2 rounded-lg bg-muted/50 px-3 py-2.5 text-xs text-muted-foreground">
            <Mail className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{infoText}</span>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border pt-4">
          <span className="text-sm text-muted-foreground">
            {t('invite_summary', { count, role: roleLabel })}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleClose}>
              {t('cancel')}
            </Button>
            <Button onClick={handleSend} disabled={submitting || sendDisabled || (count === 0 && !detectRecipient(draft))}>
              {t('invite_send_count', { count: count || 1 })}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default InviteMembersModal;
